'use client';

import DashboardSidebar from '@/components/shared/layouts/DashboardSidebar';
import { LoadingSpinner } from '@/components/ui';
import { useAuth } from '@/contexts/AuthContext';
import { getStoredJourneyUserType } from '@/lib/utils/journeyUserType';
import { useLocale } from 'next-intl';
import { memo, Suspense, useEffect, useRef, useState } from 'react';

const SIDEBAR_USER_TYPE_STORAGE_KEY = '__sidebar_user_type__';

function getStoredSidebarUserType(): 'client' | 'supplier' | null {
  if (typeof window === 'undefined') return null;
  const token = localStorage.getItem('token');
  if (!token) return null;
  const storedUserType = localStorage.getItem(SIDEBAR_USER_TYPE_STORAGE_KEY);
  if (storedUserType === 'client' || storedUserType === 'supplier') {
    return storedUserType;
  }
  return null;
}

function ClientLayoutContent({ children }: { children: React.ReactNode }) {
  const locale = useLocale();
  const isRTL = locale === 'ar';
  const { user, isLoading } = useAuth();
  // نبدأ دائماً بـ null لتجنب hydration mismatch (السيرفر لا يملك localStorage)
  const [sidebarUserType, setSidebarUserType] = useState<'client' | 'supplier' | null>(null);
  const isInitialized = useRef(false);
  const isInJourneyMode = useRef(false);

  // التهيئة الأولية - مرة واحدة فقط
  useEffect(() => {
    if (isInitialized.current) return;

    // أولاً: التحقق من localStorage (journey mode)
    const storedJourneyType = getStoredJourneyUserType();
    if (storedJourneyType === 'client' || storedJourneyType === 'supplier') {
      setTimeout(() => setSidebarUserType(storedJourneyType), 0);
      isInJourneyMode.current = true;
      isInitialized.current = true;
      return;
    }

    // ثانياً: استخدام user type من auth (normal mode)
    if (user?.userType === 'client' || user?.userType === 'supplier') {
      const userType = user.userType; // Extract to allow TypeScript narrowing
      setTimeout(() => setSidebarUserType(userType), 0);
      localStorage.setItem(SIDEBAR_USER_TYPE_STORAGE_KEY, userType);
      isInJourneyMode.current = false;
      isInitialized.current = true;
      return;
    }

    // لا يوجد نوع مستخدم - لا تظهر Sidebar
    isInitialized.current = true;
  }, [user?.userType]);

  // مراقبة تغييرات journey type
  useEffect(() => {
    const handleJourneyTypeChange = (e: CustomEvent<{ userType: string | null }>) => {
      if (!isInitialized.current) return;

      const newUserType = e.detail.userType;

      if (newUserType === 'client' || newUserType === 'supplier') {
        setSidebarUserType(newUserType);
        isInJourneyMode.current = true;
      } else if (newUserType === null) {
        if (user?.userType === 'client' || user?.userType === 'supplier') {
          const userType = user.userType; // Extract to allow TypeScript narrowing
          setSidebarUserType(userType);
        } else {
          setSidebarUserType(null);
        }
        isInJourneyMode.current = false;
      }
    };

    window.addEventListener('journeyUserTypeChanged', handleJourneyTypeChange as EventListener);

    return () => {
      window.removeEventListener(
        'journeyUserTypeChanged',
        handleJourneyTypeChange as EventListener
      );
    };
  }, [user?.userType]);

  // تحديث Sidebar عند تغيير user من auth (فقط في الوضع العادي)
  useEffect(() => {
    if (!isInitialized.current) return;
    if (isInJourneyMode.current) return;

    if (user?.userType === 'client' || user?.userType === 'supplier') {
      const userType = user.userType; // Extract to allow TypeScript narrowing
      setTimeout(() => setSidebarUserType(userType), 0);
      localStorage.setItem(SIDEBAR_USER_TYPE_STORAGE_KEY, userType);
    } else {
      if (!isLoading) {
        setTimeout(() => setSidebarUserType(null), 0);
        localStorage.removeItem(SIDEBAR_USER_TYPE_STORAGE_KEY);
      }
    }
  }, [user?.userType, isLoading]);

  // إظهار Sidebar فقط للمستخدمين المسجلين (client/supplier)
  const hasSidebar = sidebarUserType === 'client' || sidebarUserType === 'supplier';

  return (
    <div className="flex min-h-screen">
      {hasSidebar && sidebarUserType && <DashboardSidebar userType={sidebarUserType} />}
      <main
        id="main-content"
        tabIndex={-1}
        className={`min-w-0 flex-1 overflow-x-hidden ${hasSidebar ? (isRTL ? 'lg:mr-[var(--sidebar-width,280px)]' : 'lg:ml-[var(--sidebar-width,280px)]') : ''} pb-20 lg:pb-0`}
      >
        <Suspense
          fallback={
            <div className="flex h-64 items-center justify-center">
              <LoadingSpinner size="lg" />
            </div>
          }
        >
          {children}
        </Suspense>
      </main>
    </div>
  );
}

export default memo(ClientLayoutContent);
